<template>
  <div class="auth-wrapper"></div>
</template>

<script>
  import { checkForUnsafeChars } from "@/assets/utilities/helpers/sanitization";
  import themeConfig from "@themeConfig";
  import useAppConfig from "@core/@app-config/useAppConfig";
  import { mapActions, mapGetters } from "vuex";

  export default {
    data() {
      const { appSkinVariant } = useAppConfig();

      return {
        appSkinVariant,
        appName: themeConfig.app.name,
        appLogo: themeConfig.app.logo,
        ownershipTransferToken: "",
        organizationId: "",
        loading: false,
        organization: null,
      };
    },
    computed: {
      ...mapGetters({
        currentUser: "getCurrentUser",
        getActiveOrganization: "getActiveOrganization",
        getUserOrganizations: "getUserOrganizations",
      }),
    },
    async mounted() {
      this.ownershipTransferToken = this.$route.params.token;
      this.organizationId = this.$route.params.organizationId;
      // Check for unsafe characters in route parameters
      if (!checkForUnsafeChars(this.ownershipTransferToken) && !checkForUnsafeChars(this.organizationId)) {
        await this.redirect({
          success: false,
          message: this.$t("generic.error"),
        });
      }
      // Check if organization exists by provided organizationId (via route params)
      this.organization = await this.getOrganization();
      /**
       * In case organization exists by provided organization ID, but confirmation fails, we need to set
       * currently active organization to the one provided via url, so that we redirect to that organization page
       */
      this.setCurrentOrganization();
      const ownershipTransferConfirmed = await this.confirmOwnershipTransfer();
      // Ownership transfered, now we need to update vuex state to obtain correct permissions of the user
      if (ownershipTransferConfirmed) {
        /**
         * Since ownership transfer is confirmed now, current user is not an owner anymore (In back-end),
         * But in vuex state we still have old activeOrganization, which means that in client-side vuex state,
         * user is still an owner. This is an issue because after redirecting to organization edit page, user will still
         * be able to see button to transfer ownership, which he shouldn't.
         */
        await this.refreshCurrentlyActiveOrganizationData(this.organization.id);
        await this.redirect({
          success: true,
          message: this.$t("resources.organizations.transfer_ownership_confirmed"),
        });
      }
    },
    methods: {
      ...mapActions({
        refreshCurrentlyActiveOrganizationData: "refreshCurrentlyActiveOrganizationData",
      }),
      setCurrentOrganization() {
        const userOrganization = this.getUserOrganizations.find(item => item.organization.id === this.organization?.id);

        if (userOrganization) {
          this.$store.dispatch("setActiveOrganization", userOrganization);
          this.$store.dispatch("updateAbilities");
        }
      },

      async redirect(params = {}) {
        const redirectOrganizationId = this.organization?.id || this.getActiveOrganization.organization.id;
        await this.$router
          .push({
            name: "settings",
            query: { tab: "workplace_users" },
            params: {
              id: redirectOrganizationId,
              ...params,
            },
          })
          .catch(err => err);
      },

      async confirmOwnershipTransfer() {
        try {
          await this.$admin.dataProvider.customHttpCall(
            "POST",
            `organizations/${this.organization.id}/change_ownership_confirm?own=${this.ownershipTransferToken}`,
            {},
          );

          return true;
        } catch (err) {
          await this.redirect({
            success: false,
            message: this.$t("generic.error"),
          });
        }
      },

      async getOrganization() {
        try {
          const response = await this.$admin.dataProvider.getOne("organizations", {
            id: this.organizationId,
          });

          return response.data;
        } catch (err) {
          return await this.redirect({
            success: false,
            message: err.message,
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped></style>
